import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Clock, CheckSquare, FolderKanban, TrendingUp, Calendar } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  StatusBadge,
  Badge,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  PriorityBadge,
} from '@/components/ui';
import { EmployeeFormModal, TaskFormModal } from '@/components/forms';
import {
  useEmployee,
  useEmployeeStats,
  useEmployeeProjects,
  useEmployeeTasks,
} from '@/hooks/useEmployees';
import { useAuthStore } from '@/stores/auth';
import { EMPLOYEE_ROLE_LABELS, formatDate } from '@aelita/shared';
import type { Employee, Task } from '@aelita/shared';

export const Route = createFileRoute('/employee/$employeeId')({
  component: EmployeeDashboard,
});

function EmployeeDashboard() {
  const { employeeId } = Route.useParams();
  const { data: employee, isLoading } = useEmployee(employeeId);
  const { data: stats } = useEmployeeStats(employeeId);
  const { data: projects } = useEmployeeProjects(employeeId);
  const { data: tasks } = useEmployeeTasks(employeeId);
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  // Modal states
  const [isEditEmployeeOpen, setIsEditEmployeeOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">Loading employee details...</p>
        </PageContainer>
      </>
    );
  }

  if (!employee) {
    return (
      <>
        <Header title="Employee Not Found" />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">The employee you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/employees" className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block">
            Back to Employees
          </Link>
        </PageContainer>
      </>
    );
  }

  const emp = employee as any;
  const projectList = (projects as any[]) ?? [];
  const taskList = (tasks as any[]) ?? [];

  // Stats from API
  const totalHours = stats?.totalHours ?? 0;
  const hoursThisWeek = stats?.hoursThisWeek ?? 0;
  const hoursThisMonth = stats?.hoursThisMonth ?? 0;
  const billableHoursThisMonth = stats?.billableHoursThisMonth ?? 0;
  const activeProjects = stats?.activeProjects ?? projectList.length;
  const totalTasks = stats?.totalTasks ?? taskList.length;
  const tasksByStatus = stats?.tasksByStatus ?? {};
  const recentTimeEntries = stats?.recentTimeEntries ?? [];

  // Calculate derived stats
  const completedTasks = tasksByStatus.done ?? 0;
  const _inProgressTasks = tasksByStatus.in_progress ?? 0;
  const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const billableRate =
    hoursThisMonth > 0 ? Math.round((billableHoursThisMonth / hoursThisMonth) * 100) : 0;

  // Get overdue tasks
  const now = new Date();
  const overdueTasks = taskList.filter(
    (t: any) =>
      t.dueDate && new Date(t.dueDate) < now && t.status !== 'done' && t.status !== 'cancelled',
  );

  // Active projects only
  const activeProjectList = projectList.filter(
    (p: any) => p.status === 'active' || p.status === 'pipeline',
  );

  return (
    <>
      <Header
        title={`${emp.firstName} ${emp.lastName}`}
        subtitle={
          <div className="flex items-center gap-2">
            <span>{emp.title ?? emp.email}</span>
            <Badge variant="primary">
              {EMPLOYEE_ROLE_LABELS[emp.role as keyof typeof EMPLOYEE_ROLE_LABELS] ?? emp.role}
            </Badge>
            <StatusBadge status={emp.status} />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/employees">
              <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            {isAdmin && <Button onClick={() => setIsEditEmployeeOpen(true)}>Edit Profile</Button>}
          </div>
        }
      />
      <PageContainer>
        {/* Key Metrics Row */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-950 rounded-lg">
                <Clock className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{hoursThisWeek.toFixed(1)}h</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">This Week</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{hoursThisMonth.toFixed(1)}h</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">This Month</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg">
                <FolderKanban className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeProjects}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Active Projects</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-950 rounded-lg">
                <CheckSquare className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalTasks - completedTasks}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Open Tasks</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-950 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{billableRate}%</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Billable Rate</p>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Employee Info */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Profile Information</h2>
              </CardHeader>
              <CardBody>
                <div className="flex items-start gap-6 mb-6">
                  <Avatar
                    src={emp.avatarUrl}
                    name={`${emp.firstName} ${emp.lastName}`}
                    size="lg"
                    className="h-24 w-24 text-3xl"
                  />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {emp.firstName} {emp.lastName}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">{emp.title}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="primary">
                        {EMPLOYEE_ROLE_LABELS[emp.role as keyof typeof EMPLOYEE_ROLE_LABELS] ??
                          emp.role}
                      </Badge>
                      <StatusBadge status={emp.status} />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{emp.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{totalHours.toFixed(0)}h</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Hours Logged</p>
                  </div>
                </div>

                <dl className="grid grid-cols-3 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Department</dt>
                    <dd className="mt-1 font-medium">{emp.department ?? '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                    <dd className="mt-1 font-medium">{emp.phone ?? '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Hire Date</dt>
                    <dd className="mt-1 font-medium">
                      {emp.hireDate ? formatDate(new Date(emp.hireDate)) : '-'}
                    </dd>
                  </div>
                </dl>
              </CardBody>
            </Card>

            {/* Time Stats */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Time Summary</h2>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-2xl font-bold">{hoursThisWeek.toFixed(1)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Hours This Week</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-2xl font-bold">{hoursThisMonth.toFixed(1)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Hours This Month</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-2xl font-bold">{billableHoursThisMonth.toFixed(1)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Billable (Month)</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-2xl font-bold">{totalHours.toFixed(0)}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Hours</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Overdue Tasks */}
            {overdueTasks.length > 0 && (
              <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
                <CardHeader>
                  <h2 className="font-semibold text-red-800 dark:text-red-300">
                    Overdue Tasks ({overdueTasks.length})
                  </h2>
                </CardHeader>
                <CardBody className="space-y-2">
                  {overdueTasks.slice(0, 5).map((task: any) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded border border-red-100 dark:border-red-800"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Due {formatDate(new Date(task.dueDate))}
                        </p>
                        {task.project && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{task.project.name}</p>
                        )}
                      </div>
                      <PriorityBadge priority={task.priority} />
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            {/* Assigned Tasks */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Assigned Tasks ({taskList.length})</h2>
              </CardHeader>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Task</TableHeader>
                    <TableHeader>Project</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Priority</TableHeader>
                    <TableHeader>Due Date</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskList.length > 0 ? (
                    taskList.slice(0, 10).map((task: any) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <button
                            onClick={() => setEditingTask(task)}
                            className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 text-left"
                          >
                            {task.title}
                          </button>
                        </TableCell>
                        <TableCell>
                          {task.project ? (
                            <Link
                              to="/project/$projectId"
                              params={{ projectId: task.project.id }}
                              className="text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {task.project.name}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={task.status} />
                        </TableCell>
                        <TableCell>
                          <PriorityBadge priority={task.priority} />
                        </TableCell>
                        <TableCell>
                          {task.dueDate ? (
                            <span
                              className={
                                new Date(task.dueDate) < now && task.status !== 'done'
                                  ? 'text-red-600 dark:text-red-400'
                                  : ''
                              }
                            >
                              {formatDate(new Date(task.dueDate))}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 dark:text-gray-400">
                        No tasks assigned
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {taskList.length > 10 && (
                <div className="p-3 border-t text-center">
                  <Link
                    to="/tasks"
                    search={{ assigneeId: employeeId }}
                    className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                  >
                    View all {taskList.length} tasks
                  </Link>
                </div>
              )}
            </Card>

            {/* Recent Activity */}
            {recentTimeEntries.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Recent Time Entries</h2>
                </CardHeader>
                <CardBody className="space-y-3">
                  {recentTimeEntries.slice(0, 5).map((entry: any) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{entry.project?.name}</p>
                        {entry.task && <p className="text-sm text-gray-600 dark:text-gray-300">{entry.task.title}</p>}
                        {entry.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                            {entry.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{(entry.duration / 60).toFixed(1)}h</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(new Date(entry.startTime))}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Distribution */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Task Status</h2>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{taskCompletionRate}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Completion Rate</p>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Done', value: tasksByStatus.done ?? 0, color: 'bg-green-500' },
                    {
                      label: 'In Progress',
                      value: tasksByStatus.in_progress ?? 0,
                      color: 'bg-blue-500',
                    },
                    {
                      label: 'In Review',
                      value: tasksByStatus.review ?? 0,
                      color: 'bg-purple-500',
                    },
                    { label: 'To Do', value: tasksByStatus.todo ?? 0, color: 'bg-yellow-500' },
                    { label: 'Backlog', value: tasksByStatus.backlog ?? 0, color: 'bg-gray-400' },
                  ]
                    .filter((item) => item.value > 0)
                    .map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-300">{item.label}</span>
                          <span className="font-medium">{item.value}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`${item.color} h-2 rounded-full`}
                            style={{
                              width: totalTasks > 0 ? `${(item.value / totalTasks) * 100}%` : '0%',
                            }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </CardBody>
            </Card>

            {/* Projects */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Projects ({activeProjectList.length} active)</h2>
              </CardHeader>
              <CardBody className="space-y-3">
                {activeProjectList.length > 0 ? (
                  activeProjectList.map((project: any) => (
                    <div key={project.id} className="flex items-center justify-between">
                      <div>
                        <Link
                          to="/project/$projectId"
                          params={{ projectId: project.id }}
                          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          {project.name}
                        </Link>
                        {project.assignment?.role && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{project.assignment.role}</p>
                        )}
                      </div>
                      <StatusBadge status={project.status} />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No active projects</p>
                )}
              </CardBody>
            </Card>

            {/* Skills */}
            {emp.skills?.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Skills</h2>
                </CardHeader>
                <CardBody>
                  <div className="flex flex-wrap gap-2">
                    {emp.skills.map((skill: string) => (
                      <Badge key={skill} variant="gray">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Manager */}
            {emp.manager && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Reports To</h2>
                </CardHeader>
                <CardBody>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={emp.manager.avatarUrl}
                      name={`${emp.manager.firstName} ${emp.manager.lastName}`}
                    />
                    <div>
                      <Link
                        to="/employee/$employeeId"
                        params={{ employeeId: emp.manager.id }}
                        className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {emp.manager.firstName} {emp.manager.lastName}
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{emp.manager.email}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Direct Reports */}
            {emp.directReports?.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold">Direct Reports ({emp.directReports.length})</h2>
                </CardHeader>
                <CardBody className="space-y-3">
                  {emp.directReports.map((report: any) => (
                    <div key={report.id} className="flex items-center gap-3">
                      <Avatar
                        src={report.avatarUrl}
                        name={`${report.firstName} ${report.lastName}`}
                        size="sm"
                      />
                      <div>
                        <Link
                          to="/employee/$employeeId"
                          params={{ employeeId: report.id }}
                          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          {report.firstName} {report.lastName}
                        </Link>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{report.title}</p>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </PageContainer>

      {/* Edit Employee Modal (Admin only) */}
      {isAdmin && (
        <EmployeeFormModal
          isOpen={isEditEmployeeOpen}
          onClose={() => setIsEditEmployeeOpen(false)}
          employee={employee as Employee}
        />
      )}

      {/* Edit Task Modal */}
      <TaskFormModal
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
      />
    </>
  );
}
